import { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import cls from 'classnames';

import { ViewMode } from '@cutwise/carbon-viewer/lib/constants';
import { throttle, useToggle } from '@cutwise/utils';

import CrownIcon from '@/assets/icons/icon-4v-crown.svg?react';
import PavillionIcon from '@/assets/icons/icon-4v-pavillion.svg?react';
import SideViewTableRightIcon from '@/assets/icons/icon-4v-right.svg?react';
import SideViewTableUpIcon from '@/assets/icons/icon-4v-side-view-table-up.svg?react';
import FingerPointIcon from '@/assets/icons/icon-finger-point.svg?react';
import GyroIcon from '@/assets/icons/icon-gyro.svg?react';
import ResetCameraIcon from '@/assets/icons/icon-reset-camera.svg?react';
import ZoomInIcon from '@/assets/icons/icon-zoom-in.svg?react';
import ZoomOutIcon from '@/assets/icons/icon-zoom-out.svg?react';
import { LensFValue } from '@/constants/lensFValues';
import { useStore } from '@/stores/StoreProvider';
import SpinnerLoader from '@/ui-kit/SpinnerLoader/SpinnerLoader';

import LensFNumberButton from './LensFNumberButton/LensFNumberButton';
import styles from './PhotorealViewer.module.scss';

let last = 0; // timestamp of the last render() call

let startTime = 0; // timestamp of animation start
let prevTime = 0; // timestamp of previous frame during animation

let counterSec = 0;
let animation: number = 0;

let isAnimating = false;

function PhotorealViewer() {
  const {
    diamond,
    viewMode,
    setViewMode,
    resetCamera,
    rotateCamera,
    applySettings,
    blockRender,
    setCameraView,
    zoomIn,
    zoomOut,
    toggleGyro,
    isDebug,
    startCarbonViewer,
    carbonViewer,
    viewerError,
    isCarbonViewerLoaded,
  } = useStore('diamondsStore');

  const [isFocused, focus, blur] = useToggle(true);
  const [isInitiallyTouched, setInitiallyTouched] = useToggle(Boolean(localStorage.getItem('isTouched')));
  const [isPointerVisible, showPointerVisible, closePointerVisible] = useToggle(false);
  const [lensFNumber, setLensFNumber] = useState<LensFValue>(LensFValue.F28);

  const [coef, setCoef] = useState(0);

  const [isIntersecting, setIntersecting] = useState(false);
  const [shouldRenderLoader, setShouldRenderLoader] = useState(true);

  function onChangeViewMode(nextViewMode: string) {
    if (!isCarbonViewerLoaded) {
      return;
    }

    if (nextViewMode === viewMode) {
      return;
    }

    if (nextViewMode === ViewMode.Office && diamond?.isFancyColor) {
      nextViewMode = ViewMode.OfficeFancyColor;
    }

    setViewMode(nextViewMode);
  }

  function onChangeLensFNumber(lensFNumber: LensFValue) {
    applySettings({ lensFNumber });

    setLensFNumber(lensFNumber);
  }

  function renderLoader() {
    let previewURL;

    if ((viewMode === ViewMode.Office || viewMode === ViewMode.OfficeFancyColor) && officePreview) {
      previewURL = officePreview.url;
    }

    if (viewMode === ViewMode.Fire && firePreview) {
      previewURL = firePreview.url;
    }

    if (viewMode === ViewMode.ASETBlack && asetBlackPreview) {
      previewURL = asetBlackPreview.url;
    }

    return (
      <div
        className={cls(styles.loader, {
          [styles.fancyPreload]: viewMode === ViewMode.OfficeFancyColor,
        })}
      >
        {previewURL && <img height="100%" src={previewURL} alt="Photoreal Preview" />}

        <div className={styles.spinner}>
          <p>Loading 3d experience</p>

          <SpinnerLoader size="24" />
        </div>
      </div>
    );
  }

  function resetAnimation() {
    isAnimating = false;
    counterSec = 0;

    setCoef(0);
    resetCamera();

    closePointerVisible();
  }

  function animate(now = 0) {
    if (!last || now - last >= 1000) {
      last = now;

      if (counterSec < 5 && counterSec + 1 >= 5) {
        // just started animation
        startTime = now;
        prevTime = now;
      }

      counterSec += 1;
    }

    if (carbonViewer?.isRenderBlocked) {
      resetAnimation();
    } else if (counterSec >= 5) {
      isAnimating = true;
      showPointerVisible();

      applySettings({ preciseFastMode: 'fast' });

      const timeSec = (now - startTime) / 1000.0;
      const prevTimeSec = (prevTime - startTime) / 1000.0;

      prevTime = now;

      const periodSec = 1.25;
      const amplitudeRad = 0.2;

      const angleNew = amplitudeRad * Math.sin(Math.PI * (1.0 / periodSec) * timeSec);
      const anglePrev = amplitudeRad * Math.sin(Math.PI * (1.0 / periodSec) * prevTimeSec);

      setCoef(-(angleNew * 25));

      rotateCamera(angleNew - anglePrev);

      if (timeSec >= periodSec * 3.0) {
        resetAnimation();
      }
    }

    animation = window.requestAnimationFrame(animate);
  }

  function onTouchInteraction() {
    localStorage.setItem('isTouched', '1');

    setInitiallyTouched();

    window.cancelAnimationFrame(animation);
  }

  const onScroll = useCallback(() => {
    // when scroll page block CV render to avoid freezing (because CV require a lot of resources)
    blockRender(!isAnimating);

    throttle(() => {
      blockRender(!isIntersecting);
    }, 150);
  }, [isIntersecting]);

  useEffect(() => {
    startCarbonViewer();

    const root = document.getElementById('photoreal-root');

    if (root) {
      new IntersectionObserver(([entry]) => setIntersecting(entry.isIntersecting))?.observe(root as Element);
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        setIntersecting(false);
      } else {
        setIntersecting(true);
      }
    });

    window.addEventListener('focus', focus);
    window.addEventListener('blur', blur);

    return () => {
      window.removeEventListener('focus', focus);
      window.removeEventListener('blur', blur);

      setShouldRenderLoader(true);
    };
  }, []);

  useEffect(() => {
    window.addEventListener('wheel', onScroll);
    document.getElementById('diamond-page')?.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('wheel', onScroll);
      document.getElementById('diamond-page')?.removeEventListener('scroll', onScroll);
    };
  }, [onScroll]);

  useEffect(() => {
    if (isCarbonViewerLoaded) {
      if (isFocused && !isInitiallyTouched) {
        animate();
      } else {
        // reset animation
        last = 0;
        startTime = 0;
        prevTime = 0;
        counterSec = 0;
        animation = 0;
        isAnimating = false;
        resetCamera();
        setCoef(0);
        closePointerVisible();
        window.cancelAnimationFrame(animation);
      }

      setShouldRenderLoader(false);
    }
  }, [isFocused, isCarbonViewerLoaded, isInitiallyTouched]);

  const [officePreview, firePreview, asetBlackPreview] = useMemo(
    () => [diamond?.lightningPreview('Office'), diamond?.lightningPreview('Fire'), diamond?.lightningPreview('ASET Black')],
    [],
  );

  return (
    <div id="photoreal-root" className={styles.root}>
      <div className={cls(styles.header, 'container')}>
        <h2 className={styles.title}>
          {!diamond?.isFancyColor && viewMode === ViewMode.Fire && <span>Fire</span>}

          {(viewMode === ViewMode.Office || viewMode === ViewMode.OfficeFancyColor) && <span>Brilliance</span>}

          {viewMode === ViewMode.ASETBlack && <span>Optical Symmetry</span>}

          {diamond?.virtual && <span className={styles.photorealLabel}>Photoreal</span>}
        </h2>
      </div>

      <div className={cls(styles.content, 'container')} onMouseDown={onTouchInteraction} onTouchStart={onTouchInteraction}>
        {isDebug && (
          <div className={styles.debug}>
            <div>state: {`${carbonViewer?.state}`}</div>

            <div>isRenderBlocked: {`${carbonViewer?.isRenderBlocked}`}</div>

            <div>version: {`${carbonViewer?.version}`}</div>

            <div>Wasm heap size: : {`${carbonViewer?.wasmHeapSize}`}</div>

            {viewerError && <div>error: {viewerError}</div>}
          </div>
        )}

        {viewerError && <div className={styles.error}>Oops! Something went wrong. Please try to refresh the page or come back here later.</div>}

        {shouldRenderLoader && renderLoader()}

        {isDebug && (
          <div className={styles.leftControls}>
            <button onClick={toggleGyro} className={cls({ [styles.activeControls]: Boolean(carbonViewer?.isGyro) })}>
              <GyroIcon width="25" height="25" />
            </button>
          </div>
        )}

        <div className={styles.viewer} id="container"></div>

        {!viewerError && (
          <div
            className={cls(styles.rightControls, {
              [styles.rightControlsDisabled]: !isCarbonViewerLoaded,
              [styles.fancyOfficeControls]: viewMode === ViewMode.OfficeFancyColor,
            })}
          >
            <button onClick={resetCamera}>
              <ResetCameraIcon width="25" height="25" />
            </button>

            <button onClick={() => setCameraView('crown')}>
              <CrownIcon width="25" height="25" />
            </button>

            <button onClick={() => setCameraView('pavilion')}>
              <PavillionIcon width="25" height="25" />
            </button>

            <button onClick={() => setCameraView('table-up')}>
              <SideViewTableUpIcon width="25" height="25" />
            </button>

            <button onClick={() => setCameraView('table-left')}>
              <SideViewTableRightIcon width="25" height="25" />
            </button>

            <button onClick={zoomIn}>
              <ZoomInIcon width="25" height="25" />
            </button>

            <button onClick={zoomOut}>
              <ZoomOutIcon width="25" height="25" />
            </button>

            <LensFNumberButton lensFNumber={lensFNumber} onSelectFNumber={onChangeLensFNumber} />
          </div>
        )}

        {!isInitiallyTouched && isPointerVisible && (
          <div className={styles.interactionIcon} style={{ left: `${50 + coef}%` }}>
            <FingerPointIcon width="50" height="50" />
          </div>
        )}
      </div>

      <div className={cls(styles.pagination, 'container')}>
        <div
          className={cls(styles.pagerContent, {
            [styles.pagerContentLoader]: !isCarbonViewerLoaded,
          })}
        >
          {[ViewMode.Fire, ViewMode.Office, ViewMode.ASETBlack].map((mode) => {
            if (diamond?.isFancyColor && mode === ViewMode.Fire) {
              return null;
            }

            return (
              <div
                key={mode}
                className={cls(styles.viewMode, {
                  [styles.viewModeActive]: viewMode === mode,
                })}
              >
                <div className={styles.viewModeWrapper}>
                  <div
                    className={cls(styles.viewModeWrapperContent, {
                      [styles.viewModeWrapperDisabled]: !isCarbonViewerLoaded || viewerError,
                    })}
                    onClick={() => onChangeViewMode(mode)}
                  >
                    {mode === ViewMode.Office && (
                      <div className={styles.viewModePreview}>
                        {officePreview && <img height="62" src={officePreview.url} alt="Office Preview" />}

                        {!officePreview && <span>Office</span>}
                      </div>
                    )}

                    {mode === ViewMode.Fire && (
                      <div className={styles.viewModePreview}>
                        {firePreview && <img height="62" src={firePreview.url} alt="Fire Preview" />}

                        {!firePreview && <span>Fire</span>}
                      </div>
                    )}

                    {mode === ViewMode.ASETBlack && (
                      <div className={styles.viewModePreview}>
                        {asetBlackPreview && <img height="62" src={asetBlackPreview.url} alt="ASET Black Preview" />}

                        {!asetBlackPreview && <span>ASET Black</span>}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default observer(PhotorealViewer);
